'use client'

import { Sparkles, Brain, Zap, Target, Shield, Rocket } from 'lucide-react'
import Header from '@/components/header'
import { cn } from '@/lib/utils'
import { useEffect, useState } from 'react'
import Link from 'next/link'

export default function Home() {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const features = [
    {
      icon: Brain,
      title: '智能对话',
      description: '基于先进的AI模型，提供自然流畅的对话体验，深度理解您的需求',
      gradient: 'from-primary to-secondary',
    },
    {
      icon: Zap,
      title: '极速响应',
      description: '毫秒级响应速度，实时流式输出，让AI助手成为您的得力帮手',
      gradient: 'from-secondary to-accent',
    },
    {
      icon: Target,
      title: '精准理解',
      description: '多轮对话上下文记忆，准确把握对话意图，提供精准答案',
      gradient: 'from-accent to-chart-4',
    },
    {
      icon: Shield,
      title: '安全可靠',
      description: '企业级数据加密，严格的隐私保护机制，确保您的数据安全',
      gradient: 'from-chart-4 to-chart-5',
    },
    {
      icon: Rocket,
      title: '持续进化',
      description: '模型不断更新优化，功能持续迭代升级，始终保持领先',
      gradient: 'from-chart-5 to-primary',
    },
    {
      icon: Sparkles,
      title: '多场景应用',
      description: '覆盖教育、办公、创作等多个场景，一个平台满足所有需求',
      gradient: 'from-primary to-accent',
    },
  ]

  return (
    <>
      <Header />
      <main className='min-h-screen bg-background relative overflow-hidden'>
        {/* 动态背景 - 浮动几何图形 */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          {mounted && (
            <>
              {/* 大圆形 */}
              <div
                className='absolute w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float'
                style={{
                  top: '10%',
                  left: '5%',
                  animationDelay: '0s',
                  animationDuration: '20s',
                }}
              />
              <div
                className='absolute w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float'
                style={{
                  top: '60%',
                  right: '10%',
                  animationDelay: '5s',
                  animationDuration: '25s',
                }}
              />
              <div
                className='absolute w-64 h-64 bg-accent/5 rounded-full blur-3xl animate-float'
                style={{
                  bottom: '10%',
                  left: '15%',
                  animationDelay: '10s',
                  animationDuration: '30s',
                }}
              />

              {/* 小方块 */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className='absolute w-3 h-3 bg-primary/10 rounded animate-float-reverse'
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${i * 2}s`,
                    animationDuration: `${15 + i * 2}s`,
                  }}
                />
              ))}
            </>
          )}
        </div>

        {/* Hero区域 */}
        <section className='relative pt-20 pb-32 px-4 sm:pt-32 sm:pb-40'>
          <div className='max-w-7xl mx-auto'>
            <div className='text-center space-y-8 animate-fade-in'>
              {/* 标签 */}
              <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-slide-up'>
                <Sparkles className='w-4 h-4 text-primary' />
                <span className='text-sm font-medium text-primary'>
                  AI驱动的智能助教平台
                </span>
              </div>

              {/* 主标题 */}
              <h1 className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight animate-slide-up'>
                开启
                <span className='bg-linear-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradient'>
                  {' '}
                  AI智能{' '}
                </span>
                <br />
                教育新时代
              </h1>

              {/* 副标题 */}
              <p className='text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed animate-slide-up'>
                融合前沿AI技术与教育场景，打造智能化、个性化的思政教学助手
                <br />
                让每一次对话都成为知识的传递，让每一次互动都充满智慧
              </p>

              {/* CTA按钮 */}
              <div className='flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-slide-up'>
                <Link
                  href='/light'
                  className='text-gray-700 hover:text-red-600 px-4 py-2 rounded font-medium transition-colors flex items-center gap-2'
                >
                  <button className='group relative px-8 py-4 rounded-lg bg-linear-to-r from-primary to-secondary text-white font-medium text-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden'>
                    <span className='relative z-10 flex items-center gap-2'>
                      立即体验
                      <Rocket className='w-5 h-5 group-hover:translate-x-1 transition-transform' />
                    </span>
                    <div className='absolute inset-0 bg-linear-to-r from-secondary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
                  </button>
                </Link>

                <button className='px-8 py-4 rounded-lg border-2 border-primary text-primary font-medium text-lg transition-all duration-300 hover:bg-primary hover:text-white hover:shadow-xl hover:scale-105'>
                  了解更多
                </button>
              </div>

              {/* 数据展示 */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-6 pt-16 max-w-4xl mx-auto'>
                {[
                  { label: '活跃用户', value: '10万+' },
                  { label: '对话次数', value: '500万+' },
                  { label: '课程资源', value: '1000+' },
                  { label: '满意度', value: '98%' },
                ].map((stat, index) => (
                  <div
                    key={index}
                    className='p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 animate-slide-up'
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className='text-3xl sm:text-4xl font-bold bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent'>
                      {stat.value}
                    </div>
                    <div className='text-sm text-muted-foreground mt-2'>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features区域 */}
        <section className='relative py-20 px-4 bg-linear-to-b from-background to-muted/20'>
          <div className='max-w-7xl mx-auto'>
            {/* 标题 */}
            <div className='text-center space-y-4 mb-16'>
              <h2 className='text-3xl sm:text-4xl md:text-5xl font-bold text-foreground'>
                核心功能特性
              </h2>
              <p className='text-lg text-muted-foreground max-w-2xl mx-auto'>
                汇聚最前沿的AI技术，为教育场景量身打造
              </p>
            </div>

            {/* 功能卡片网格 */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {features.map((feature, index) => {
                const Icon = feature.icon
                return (
                  <div
                    key={index}
                    className='group relative p-8 rounded-2xl bg-card border border-border hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden animate-fade-in-up'
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* 渐变背景 - 悬停时显示 */}
                    <div
                      className={cn(
                        'absolute inset-0 bg-linear-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-500',
                        feature.gradient
                      )}
                    />

                    {/* 图标 */}
                    <div
                      className={cn(
                        'relative w-16 h-16 rounded-2xl bg-linear-to-br flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500',
                        feature.gradient
                      )}
                    >
                      <Icon className='w-8 h-8 text-white' />
                    </div>

                    {/* 内容 */}
                    <div className='relative space-y-3'>
                      <h3 className='text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300'>
                        {feature.title}
                      </h3>
                      <p className='text-muted-foreground leading-relaxed'>
                        {feature.description}
                      </p>
                    </div>

                    {/* 装饰性箭头 */}
                    <div className='absolute bottom-6 right-6 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300'>
                      <svg
                        className='w-4 h-4 text-primary'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 5l7 7-7 7'
                        />
                      </svg>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* CTA区域 */}
        <section className='relative py-20 px-4'>
          <div className='max-w-4xl mx-auto'>
            <div className='relative p-12 sm:p-16 rounded-3xl bg-linear-to-br from-primary via-secondary to-accent overflow-hidden'>
              {/* 装饰性图形 */}
              <div className='absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl' />
              <div className='absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl' />

              {/* 内容 */}
              <div className='relative text-center space-y-6 text-white'>
                <h2 className='text-3xl sm:text-4xl md:text-5xl font-bold'>
                  准备好开始了吗？
                </h2>
                <p className='text-lg sm:text-xl text-white/90 max-w-2xl mx-auto'>
                  加入我们，体验AI赋能的智能教育平台，让教学更高效，让学习更有趣
                </p>
                <div className='pt-4'>
                  <button className='px-10 py-4 rounded-lg bg-white text-primary font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 hover:bg-background'>
                    立即开始使用
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className='relative border-t border-border bg-card/50 backdrop-blur-sm'>
          <div className='max-w-7xl mx-auto px-4 py-12'>
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8'>
              {/* 产品 */}
              <div className='space-y-4'>
                <h3 className='font-bold text-foreground'>产品</h3>
                <ul className='space-y-2 text-sm text-muted-foreground'>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    AI对话
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    课程
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    经典理论
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    精神谱系
                  </li>
                </ul>
              </div>

              {/* 资源 */}
              <div className='space-y-4'>
                <h3 className='font-bold text-foreground'>资源</h3>
                <ul className='space-y-2 text-sm text-muted-foreground'>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    文档中心
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    使用指南
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    API文档
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    更新日志
                  </li>
                </ul>
              </div>

              {/* 支持 */}
              <div className='space-y-4'>
                <h3 className='font-bold text-foreground'>支持</h3>
                <ul className='space-y-2 text-sm text-muted-foreground'>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    帮助中心
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    联系我们
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    问题反馈
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    社区论坛
                  </li>
                </ul>
              </div>

              {/* 关于 */}
              <div className='space-y-4'>
                <h3 className='font-bold text-foreground'>关于我们</h3>
                <ul className='space-y-2 text-sm text-muted-foreground'>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    公司介绍
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    团队成员
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    加入我们
                  </li>
                  <li className='hover:text-primary transition-colors cursor-pointer'>
                    媒体报道
                  </li>
                </ul>
              </div>
            </div>

            {/* 底部信息 */}
            <div className='pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground'>
              <div className='flex items-center gap-2'>
                <Brain className='w-5 h-5 text-primary' />
                <span>© 2026 AI智能学习平台. All rights reserved.</span>
              </div>
              <div className='flex items-center gap-6'>
                <span className='hover:text-primary transition-colors cursor-pointer'>
                  隐私政策
                </span>
                <span className='hover:text-primary transition-colors cursor-pointer'>
                  服务条款
                </span>
                <span className='hover:text-primary transition-colors cursor-pointer'>
                  Cookie政策
                </span>
              </div>
            </div>
          </div>
        </footer>
      </main>

      {/* 自定义动画样式 */}
      <style jsx global>{`
        @keyframes float {
          0%,
          100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(30px, -30px) rotate(120deg);
          }
          66% {
            transform: translate(-20px, 20px) rotate(240deg);
          }
        }

        @keyframes float-reverse {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-20px, -20px) scale(1.2);
          }
        }

        @keyframes gradient {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-float {
          animation: float infinite ease-in-out;
        }

        .animate-float-reverse {
          animation: float-reverse infinite ease-in-out;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
          animation-fill-mode: both;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
          animation-fill-mode: both;
        }
      `}</style>
    </>
  )
}
